import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { DoorClosed, UserPlus, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";

const BG = "https://images.unsplash.com/photo-1678182451047-196f22a4143e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzaGlwcGluZyUyMHBvcnQlMjBjb250YWluZXJzJTIwZHJvbmV8ZW58MHx8fHwxNzg4MzkwOTYwfDA&ixlib=rb-4.1.0&q=85&w=1400";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [firstUser, setFirstUser] = useState(false);
  const [statusLoaded, setStatusLoaded] = useState(false);

  useEffect(() => {
    api.get("/auth/status")
      .then((r) => setFirstUser(!r.data.has_users))
      .catch(() => {})
      .finally(() => setStatusLoaded(true));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form.name, form.email, form.password);
      toast.success(u.role === "admin" ? "Cuenta de administrador creada" : "Cuenta creada");
      nav("/");
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={BG} alt="contenedores" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/75" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-emerald-500 flex items-center justify-center">
              <DoorClosed className="h-6 w-6" />
            </div>
            <span className="font-display text-xl font-bold">PuertaCotiza</span>
          </div>
          <div>
            <h1 className="font-display text-4xl font-extrabold leading-tight">Crea tu cuenta</h1>
            <p className="mt-4 text-slate-300 max-w-md">
              Registra tu equipo. El primer usuario será el administrador del sistema.
            </p>
          </div>
          <p className="text-xs text-slate-400">Uso interno · Datos confidenciales</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 bg-white">
        {statusLoaded && !firstUser ? (
          <div className="w-full max-w-sm text-center" data-testid="register-disabled">
            <h2 className="font-display text-2xl font-bold text-slate-900">Registro deshabilitado</h2>
            <p className="text-sm text-slate-500 mt-3 mb-6">
              El acceso a PuertaCotiza es solo por invitación. Solicita a un administrador que cree tu cuenta desde el panel de usuarios.
            </p>
            <Link to="/login" data-testid="go-login-link"
              className="inline-flex items-center justify-center w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold px-4 py-2.5 rounded-md transition-colors">
              Volver a iniciar sesión
            </Link>
          </div>
        ) : (
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
          <h2 className="font-display text-2xl font-bold text-slate-900">Registrarse</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">Crea una cuenta para acceder al sistema.</p>

          {firstUser && (
            <div className="flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-md px-3 py-2 mb-5">
              <ShieldCheck className="h-4 w-4" /> Serás el primer usuario, por lo tanto el <b>administrador</b>.
            </div>
          )}

          {[["name", "Nombre completo", "text"], ["email", "Email", "email"], ["password", "Contraseña", "password"]].map(([k, label, type]) => (
            <div key={k} className="mb-4">
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">{label}</label>
              <input data-testid={`register-${k}`} type={type} required value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="w-full px-3 py-2.5 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm" />
            </div>
          ))}

          <button data-testid="register-submit" type="submit" disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold py-2.5 rounded-md transition-colors disabled:opacity-60 mt-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Crear cuenta
          </button>
          <p className="text-center text-sm text-slate-500 mt-5">
            ¿Ya tienes cuenta? <Link to="/login" className="text-emerald-600 font-semibold hover:underline">Inicia sesión</Link>
          </p>
        </form>
        )}
      </div>
    </div>
  );
}
